import { useState } from "react";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupStatusDrawer from "./GroupStatusDrawer";
import Card from "../../../components/ui/Card";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useGetMyGroupsQuery } from "../../../api/groupsApi";
import { cls } from "../../hatim/soft/SoftKit";
import { weekRange } from "../../../lib/muzakereWeek";

/** Grup adının baş harfleri (avatar). */
function initials(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  return (words.slice(0, 2).map((w) => w[0]).join("") || "?").toLocaleUpperCase("tr-TR");
}

/**
 * Tek grup satırı: baş harfler, ad ve üye sayısı. Durum (sıra, tamamlama,
 * seri) satıra basınca açılan çekmecede. Ad iki satıra kadar sarar.
 */
function GroupRow({ group, onOpen, t, index = 0 }) {
  return (
    <li className="motion-safe:animate-pop-in" style={{ animationDelay: `${0.3 + Math.min(index, 8) * 0.05}s` }}>
      <button
        type="button"
        onClick={() => onOpen(group)}
        data-my-group={group.id}
        className="w-full text-left flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-terakkiBrandCloud/60 dark:hover:bg-white/[0.04] transition-colors"
      >
        <span className="w-11 h-11 rounded-full bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-white/10 dark:text-white flex items-center justify-center shrink-0 text-base font-SemiBold">
          {initials(group.groupName)}
        </span>
        <div className="flex-1 min-w-0">
          <div className={`text-md leading-snug line-clamp-2 break-words ${cls.title}`}>{group.groupName}</div>
          <div className={`text-base mt-0.5 ${cls.muted}`}>
            {group.memberCount} {t("mygroups_members")}
          </div>
        </div>
        <ChevronRightIcon fontSize="small" className={`shrink-0 ${cls.muted}`} />
      </button>
    </li>
  );
}

/**
 * Terakkim'de Hedeflerim'in yanında: üyesi olduğum gruplar. Satıra basınca
 * seçili günün haftasındaki durumum ve grubun ayrıntıları çekmecede açılır.
 */
export default function MyGroupsCard({ localDate }) {
  const { t } = useScriptMode();
  const { data, isLoading } = useGetMyGroupsQuery();
  const list = data?.data?.groups || [];
  const range = weekRange(localDate);
  const [openGroup, setOpenGroup] = useState(null);

  return (
    <Card
      data-tour="groups"
      className="!p-0 overflow-hidden motion-safe:animate-rise-in [animation-delay:.24s] flex flex-col min-h-[320px] max-h-[calc(100vh-3rem)] lg:h-full lg:max-h-full lg:min-h-0"
    >
      <div className="shrink-0 flex items-center gap-2.5 px-4 py-3 bg-terakkiBrandCloud border-b border-terakkiBrandMist">
        <span className="w-8 h-8 rounded-lg bg-terakkiPrimaryDark text-white flex items-center justify-center shrink-0">
          <GroupsOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] uppercase tracking-wide text-terakkiNeutral font-SemiBold leading-tight">
            {t("nav_my_groups")}
          </div>
          <div className="text-base font-SemiBold text-terakkiPrimaryDark leading-tight truncate">
            {isLoading ? "\u00A0" : `${list.length} ${t("mygroups_count_unit")}`}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-3 p-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`h-10 rounded ${cls.skeleton}`} />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-8 px-4">
            <GroupsOutlinedIcon sx={{ fontSize: 32 }} className="text-terakkiBorder" aria-hidden />
            <p className="text-base text-terakkiNeutral mt-2">{t("mygroups_empty")}</p>
          </div>
        ) : (
          <ul className="divide-y divide-terakkiBorder">
            {list.map((group) => (
              <GroupRow key={group.id} index={list.indexOf(group)} group={group} onOpen={setOpenGroup} t={t} />
            ))}
          </ul>
        )}
      </div>

      <GroupStatusDrawer group={openGroup} range={range} onClose={() => setOpenGroup(null)} />
    </Card>
  );
}
